import api from './api';

class PaymentScheduleNotificationService {
    constructor() {
        this.listeners = new Map();
    }

    // Subscribe to payment schedule updates for a specific booking
    subscribeToBooking(bookingId, callback) {
        if (!this.listeners.has(bookingId)) {
            this.listeners.set(bookingId, new Set());
        }
        this.listeners.get(bookingId).add(callback);

        // Return unsubscribe function
        return () => {
            const bookingListeners = this.listeners.get(bookingId);
            if (bookingListeners) {
                bookingListeners.delete(callback);
                if (bookingListeners.size === 0) {
                    this.listeners.delete(bookingId);
                }
            }
        };
    }

    // Notify all listeners for a specific booking
    notifyBooking(bookingId, eventType, data) {
        const bookingListeners = this.listeners.get(bookingId);
        if (bookingListeners) {
            bookingListeners.forEach(callback => {
                try {
                    callback(eventType, data);
                } catch (error) {
                    console.error('Error in payment schedule notification callback:', error);
                }
            });
        }
    }

    // Send milestone payment notification
    async sendMilestonePaymentNotification(bookingId, milestoneData) {
        try {
            await api.post('/notifications', {
                type: 'milestone',
                title: 'Milestone Payment Received',
                message: `Payment of ₹${milestoneData.amount?.toLocaleString()} received for milestone: ${milestoneData.name}`,
                data: {
                    booking_id: bookingId,
                    milestone_id: milestoneData.id,
                    amount: milestoneData.amount,
                    milestone_name: milestoneData.name
                }
            });
        } catch (error) {
            console.error('Failed to send milestone payment notification:', error);
        }
    }

    // Send advance payment request notification
    async sendAdvancePaymentRequestNotification(bookingId, advanceData) {
        try {
            await api.post('/notifications', {
                type: 'advance',
                title: 'Advance Payment Request',
                message: `Vendor has requested advance payment of ₹${advanceData.amount?.toLocaleString()}. Reason: ${advanceData.reason}`,
                data: {
                    booking_id: bookingId,
                    amount: advanceData.amount,
                    reason: advanceData.reason,
                    due_date: advanceData.due_date
                }
            });
        } catch (error) {
            console.error('Failed to send advance payment request notification:', error);
        }
    }

    // Send payment schedule updated notification
    async sendScheduleUpdatedNotification(bookingId, scheduleData) {
        try {
            await api.post('/notifications', {
                type: 'payment',
                title: 'Payment Schedule Updated',
                message: `Payment schedule has been updated for booking #${bookingId}`,
                data: {
                    booking_id: bookingId,
                    total_amount: scheduleData.total_amount,
                    milestones_count: scheduleData.milestones?.length || 0
                }
            });
        } catch (error) {
            console.error('Failed to send schedule updated notification:', error);
        }
    }

    // Send review submitted notification
    async sendReviewSubmittedNotification(bookingId, reviewData) {
        try {
            await api.post('/notifications', {
                type: 'review',
                title: 'New Review Received',
                message: `Customer has submitted a ${reviewData.rating}-star review for booking #${bookingId}`,
                data: {
                    booking_id: bookingId,
                    rating: reviewData.rating,
                    comment: reviewData.comment
                }
            });
        } catch (error) {
            console.error('Failed to send review submitted notification:', error);
        }
    }

    // Send overdue payment notification
    async sendOverduePaymentNotification(bookingId, milestoneData) {
        try {
            await api.post('/notifications', {
                type: 'payment',
                title: 'Overdue Payment',
                message: `Payment of ₹${milestoneData.amount?.toLocaleString()} for milestone "${milestoneData.name}" is overdue`,
                data: {
                    booking_id: bookingId,
                    milestone_id: milestoneData.id,
                    amount: milestoneData.amount,
                    due_date: milestoneData.due_date
                }
            });
        } catch (error) {
            console.error('Failed to send overdue payment notification:', error);
        }
    }

    // Check for overdue payments and send notifications
    async checkOverduePayments(bookings) {
        const today = new Date().toISOString().split('T')[0];
        
        for (const booking of bookings) {
            if (booking.payment_schedule?.milestones) {
                for (const milestone of booking.payment_schedule.milestones) {
                    if (milestone.due_date && milestone.due_date < today && !milestone.paid_at) {
                        await this.sendOverduePaymentNotification(booking.id, milestone);
                    }
                }
            }
        }
    }
}

export default new PaymentScheduleNotificationService();
